import React, { useCallback, useEffect, useState } from 'react';
import {
  Container,
  Card,
  CardBody,
  CardHeader,
  Table,
  Button,
  Alert,
  Spinner,
  Input,
} from '../shim/reactstrap';
import { useAuth } from '../contexts/AuthContext';

// Friendly labels for every notification type the backend may return.
// Keep this in sync with `WaveWise.DAL.NotificationType` and the
// notification-type registrations in
// `WaveWise.DAL/Migrations/Migration_*`. A missing entry here would
// cause the table to render the raw enum name (e.g.
// `TickersValidationCompleted`) which is unreadable for end users.
const NOTIFICATION_TYPE_LABELS = {
  NewPublicModelSignal:        'New Public Model Signal',
  NewPrivateModelSignal:       'New Private Model Signal',
  AccountVerification:         'Account Verification Email',
  ResetPassword:               'Password Reset Email',
  PasswordChanged:             'Password Changed Email',
  NewDataPackageGenerated:     'New Data Package Generated',
  TradingStrategyPendingTrade: 'Trading Strategy Pending Trade',
  TickersValidationCompleted:  'Package Tickers Validation Completed',
};

const CHANNELS = [
  { field: 'EnableInApp', label: 'In-App', hasTemplateField: 'HasInAppTemplate' },
  { field: 'EnablePushNotification', label: 'Push', hasTemplateField: 'HasPushTemplate' },
  { field: 'EnableEmail', label: 'Email', hasTemplateField: 'HasEmailTemplate' },
];

export const NotificationPreferences = () => {
  const { apiClient } = useAuth();
  const [preferences, setPreferences] = useState(null);
  const [notificationTypes, setNotificationTypes] = useState([]);
  // Map of { [NotificationType]: { HasInAppTemplate, HasPushTemplate, HasEmailTemplate } }
  const [templateAvailability, setTemplateAvailability] = useState({});
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const loadPreferences = useCallback(async () => {
    setLoadError('');
    try {
      const data = await apiClient.getNotificationPreferences();
      const map = {};
      const availability = {};
      const types = [];
      (data || []).forEach(p => {
        map[p.NotificationType] = {
          EnableInApp: p.EnableInApp,
          EnablePushNotification: p.EnablePushNotification,
          EnableEmail: p.EnableEmail,
        };
        availability[p.NotificationType] = {
          HasInAppTemplate: p.HasInAppTemplate,
          HasPushTemplate: p.HasPushTemplate,
          HasEmailTemplate: p.HasEmailTemplate,
        };
        types.push({
          key: p.NotificationType,
          label: NOTIFICATION_TYPE_LABELS[p.NotificationType] || p.NotificationType,
        });
      });
      setNotificationTypes(types);
      setPreferences(map);
      setTemplateAvailability(availability);
    } catch (err) {
      setLoadError(err?.message || 'Failed to load notification preferences.');
    }
  }, [apiClient]);

  useEffect(() => { loadPreferences(); }, [loadPreferences]);

  const handleToggle = (typeKey, field) => {
    setPreferences(prev => ({
      ...prev,
      [typeKey]: { ...prev[typeKey], [field]: !prev[typeKey][field] },
    }));
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    setSaveError('');
    setSaveSuccess(false);
    setIsSaving(true);
    try {
      const payload = notificationTypes.map(({ key }) => ({
        NotificationType: key,
        EnableInApp: preferences[key].EnableInApp,
        EnablePushNotification: preferences[key].EnablePushNotification,
        EnableEmail: preferences[key].EnableEmail,
      }));
      await apiClient.saveNotificationPreferences(payload);
      setSaveSuccess(true);
    } catch (err) {
      setSaveError(err?.message || 'Failed to save notification preferences.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Container className="mt-4">
      <div className="row justify-content-center">
        <div className="col-md-10 col-lg-9">
          <Card>
            <CardHeader>
              <h4 className="mb-0">Notification Preferences</h4>
            </CardHeader>
            <CardBody>
              {loadError && <Alert color="danger">{loadError}</Alert>}
              {saveError && <Alert color="danger">{saveError}</Alert>}
              {saveSuccess && <Alert color="success">Preferences saved.</Alert>}

              {!preferences && !loadError && (
                <div className="text-center py-3"><Spinner /></div>
              )}

              {preferences && (
                <>
                  <Table bordered responsive className="mb-3">
                    <thead>
                      <tr>
                        <th>Notification Type</th>
                        {CHANNELS.map(c => (
                          <th key={c.field} className="text-center">{c.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {notificationTypes.map(({ key, label }) => (
                        <tr key={key}>
                          <td>{label}</td>
                          {CHANNELS.map(c => {
                            const hasTemplate = templateAvailability[key]?.[c.hasTemplateField];
                            return (
                              <td key={c.field} className="text-center align-middle">
                                <Input
                                  type="checkbox"
                                  checked={preferences[key][c.field]}
                                  onChange={() => hasTemplate && handleToggle(key, c.field)}
                                  disabled={isSaving || !hasTemplate}
                                  title={!hasTemplate ? 'No template configured for this channel' : undefined}
                                />
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  <div className="d-flex justify-content-end">
                    <Button color="primary" onClick={handleSave} disabled={isSaving}>
                      {isSaving ? <Spinner size="sm" /> : 'Save'}
                    </Button>
                  </div>
                </>
              )}
            </CardBody>
          </Card>
        </div>
      </div>
    </Container>
  );
};
